// A newer brain is live, and the tab does not know — 8 Sep 2026.
//
// The brain is a static bundle behind CloudFront (claude-reference.md § AWS layout), and a release
// replaces it under every open tab without telling any of them. Vite fingerprints the bundle:
// index.html names `assets/index-<hash>.js` and is published `no-cache`, the asset for a year
// (.github/workflows/deploy.yml, the publish step). So a tab keeps running the bundle it loaded
// for as long as it is open, and keeps sending what that bundle sends. The day this was written a
// tab open since the previous evening posted a candidate's number with a space in it, four hours
// after the release that strips spaces had gone live, and the recruiter read "check the required
// fields" — T49a's fix, in a browser that had never loaded it.
//
// So the shell asks the CDN, not the service: it fetches its own index.html and reads the entry
// script it names. When that is not the script this tab loaded, a newer brain is live and a
// strip at the foot of the screen says so, with a Reload button. Polling, not sockets (CLAUDE.md
// § Brain tier), and against the CDN, so it costs the service nothing. The two rules are here,
// pure, because they are the part that can be wrong invisibly — a strip that never shows, or one
// that shows on every poll — and the hook (app/NewVersion.tsx) owns only the timer and the fetch.

/**
 * The entry script an index.html names: the `src` of its first `<script type="module">`, or null
 * when there is none (an error page, a directory listing, anything that is not the brain).
 */
export function entryScriptOf(html: string): string | null {
  for (const tag of html.matchAll(/<script\b([^>]*)>/gi)) {
    const attributes = tag[1] ?? '';
    if (!/\btype\s*=\s*["']module["']/i.test(attributes)) continue;
    const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(attributes);
    if (src?.[1]) return src[1];
  }
  return null;
}

/**
 * Whether the index.html just fetched names a different entry script from the one this tab
 * loaded. Unknown on either side is "no": the dev server names `/src/main.tsx` on both sides, the
 * harness names its own entry, and a fetch that came back as an error page names nothing.
 */
export function isNewerBuild(running: string | null, fetched: string | null): boolean {
  if (running === null || fetched === null) return false;
  return running !== fetched;
}
